import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { wsServerMessageSchema, type MailboxId } from "@mailflare/shared";

import { applyMailboxEvent, describeEvent } from "@/lib/mailbox-events";
import { mailboxWebSocketUrl, nextBackoffMs } from "@/lib/ws-client";

export type MailboxConnectionStatus = "connecting" | "connected" | "disconnected";

export interface MailboxEventsOptions {
  readonly enabled?: boolean;
  /** Called with a screen-reader announcement when new mail arrives. */
  readonly onAnnounce?: (message: string) => void;
}

function parseFrame(data: unknown): unknown {
  if (typeof data !== "string") return null;
  try {
    return JSON.parse(data);
  } catch {
    // A malformed frame is a protocol error; the schema parse below drops it.
    return null;
  }
}

/**
 * Subscribes to the mailbox real-time stream.
 *
 * - Reconnects with exponential backoff + jitter.
 * - Pauses while `document.hidden` and resumes on visibility.
 * - Applies events to the TanStack Query cache; `sync.required` invalidates all.
 */
export function useMailboxEvents(
  mailboxId: MailboxId | null,
  options: MailboxEventsOptions = {},
): MailboxConnectionStatus {
  const { enabled = true, onAnnounce } = options;
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<MailboxConnectionStatus>("disconnected");
  const announceRef = useRef(onAnnounce);
  announceRef.current = onAnnounce;

  useEffect(() => {
    if (!enabled || mailboxId === null) {
      setStatus("disconnected");
      return;
    }

    let socket: WebSocket | null = null;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;
    let paused = false;

    const clearTimer = (): void => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const connect = (): void => {
      if (disposed || paused) return;
      setStatus("connecting");
      const ws = new WebSocket(mailboxWebSocketUrl(mailboxId));
      socket = ws;

      ws.onopen = () => {
        attempt = 0;
        setStatus("connected");
        ws.send(JSON.stringify({ type: "subscribe", mailboxId }));
      };

      ws.onmessage = (event: MessageEvent) => {
        const parsed = wsServerMessageSchema.safeParse(parseFrame(event.data));
        if (!parsed.success) return;
        const frame = parsed.data;
        if (frame.type === "pong" || frame.type === "ready" || frame.type === "error") return;
        applyMailboxEvent(queryClient, frame);
        const announcement = describeEvent(frame);
        if (announcement !== null) announceRef.current?.(announcement);
      };

      ws.onclose = () => {
        socket = null;
        setStatus("disconnected");
        if (disposed || paused) return;
        const delay = nextBackoffMs(attempt);
        attempt += 1;
        clearTimer();
        timer = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    const handleVisibility = (): void => {
      if (disposed) return;
      if (document.hidden) {
        clearTimer();
        paused = true;
        socket?.close();
        socket = null;
      } else if (paused) {
        paused = false;
        attempt = 0;
        connect();
      }
    };

    connect();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      disposed = true;
      clearTimer();
      document.removeEventListener("visibilitychange", handleVisibility);
      socket?.close();
      setStatus("disconnected");
    };
  }, [mailboxId, enabled, queryClient]);

  return status;
}
